import { useCallback, useState } from "react";
import { useLazyQuery, useMutation } from "@apollo/client";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import {
  CREATE_PAYMENT_LINK,
  DEACTIVATE_PAYMENT_LINK,
  GET_PAYMENT_LINK,
  REACTIVATE_PAYMENT_LINK,
  UPDATE_PAYMENT_LINK,
} from "graphql/paymentLink";
import {
  PaymentLinkAmountMode,
  PaymentLinkStatus,
  PaymentLinkSuccessType,
  PaymentLinkType,
} from "enums/PaymentLink";
import { BillingInterval } from "enums/BillingInterval";

export interface PaymentLinkFormValues {
  name: string;
  description: string;
  linkType: PaymentLinkType;
  interval: BillingInterval;
  amountMode: PaymentLinkAmountMode;
  amount: number | null;
  minAmount: number | null;
  maxAmount: number | null;
  presetAmounts: number[];
  presetAmountsText: string;
  maxPayments: number | null;
  expiresAt: string | null;
  endsAt: string | null;
  feeCoverEnabled: boolean;
  tipEnabled: boolean;
  tipPresetPercentages: string;
  cashDiscountEnabled: boolean;
  successType: PaymentLinkSuccessType;
  successMessage: string;
  successRedirectUrl: string;
}

export interface PaymentLink {
  id: string;
  slug: string;
  url: string;
  name: string;
  description?: string;
  amountMode: PaymentLinkAmountMode;
  amount?: number;
  minAmount?: number;
  maxAmount?: number;
  presetAmounts?: number[];
  currency: string;
  linkType: PaymentLinkType;
  interval?: BillingInterval;
  maxPayments?: number;
  endsAt?: number;
  statementDescriptor?: string;
  status: PaymentLinkStatus;
  inactiveMessage?: string;
  expiresAt?: number;
  feeCover?: { enabled: boolean };
  tip?: { enabled: boolean; presetPercentages?: number[] };
  cashDiscount?: {
    enabled: boolean;
    paymentMethodTypes?: string[];
  };
  successBehavior?: {
    type: PaymentLinkSuccessType;
    message?: string;
    redirectUrl?: string;
  };
  stats?: {
    views?: number;
    checkoutsStarted?: number;
    sales?: number;
    revenue?: number;
    refunded?: number;
    netCollected?: number;
    platformFees?: number;
  };
  createdAt?: number;
}

export const emptyPaymentLinkForm = (): PaymentLinkFormValues => ({
  name: "",
  description: "",
  linkType: PaymentLinkType.ONE_TIME,
  interval: BillingInterval.MONTHLY,
  amountMode: PaymentLinkAmountMode.FIXED,
  amount: null,
  minAmount: null,
  maxAmount: null,
  presetAmounts: [],
  presetAmountsText: "",
  maxPayments: null,
  expiresAt: null,
  endsAt: null,
  feeCoverEnabled: false,
  tipEnabled: false,
  tipPresetPercentages: "",
  cashDiscountEnabled: false,
  successType: PaymentLinkSuccessType.MESSAGE,
  successMessage: "",
  successRedirectUrl: "",
});

export function toPaymentLinkForm(link: PaymentLink): PaymentLinkFormValues {
  return {
    ...emptyPaymentLinkForm(),
    name: link.name || "",
    description: link.description || "",
    linkType: link.linkType,
    interval: link.interval || BillingInterval.MONTHLY,
    amountMode: link.amountMode,
    amount: link.amount ?? null,
    minAmount: link.minAmount ?? null,
    maxAmount: link.maxAmount ?? null,
    presetAmounts: link.presetAmounts || [],
    presetAmountsText: (link.presetAmounts || []).join(", "),
    maxPayments: link.maxPayments ?? null,
    expiresAt: link.expiresAt ? new Date(link.expiresAt).toISOString() : null,
    endsAt: link.endsAt
      ? new Date(link.endsAt).toISOString().slice(0, 10)
      : null,
    feeCoverEnabled: !!link.feeCover?.enabled,
    tipEnabled: !!link.tip?.enabled,
    tipPresetPercentages: (link.tip?.presetPercentages || []).join(", "),
    cashDiscountEnabled: !!link.cashDiscount?.enabled,
    successType: link.successBehavior?.type || PaymentLinkSuccessType.MESSAGE,
    successMessage: link.successBehavior?.message || "",
    successRedirectUrl: link.successBehavior?.redirectUrl || "",
  };
}

export function usePaymentLinkData() {
  const { t } = useTranslation();
  const [paymentLink, setPaymentLink] = useState<PaymentLink | null>(null);

  const [fetchPaymentLink, { loading: loadingPaymentLink }] = useLazyQuery(
    GET_PAYMENT_LINK,
    {
      fetchPolicy: "network-only",
      onCompleted: (data) => setPaymentLink(data?.paymentLink || null),
      onError: () => toast.error(t("Could not load this payment link")),
    },
  );

  const [createPaymentLink, { loading: creating }] =
    useMutation(CREATE_PAYMENT_LINK);
  const [updatePaymentLink, { loading: updating }] =
    useMutation(UPDATE_PAYMENT_LINK);
  const [deactivatePaymentLink, { loading: deactivating }] = useMutation(
    DEACTIVATE_PAYMENT_LINK,
  );
  const [reactivatePaymentLink, { loading: reactivating }] = useMutation(
    REACTIVATE_PAYMENT_LINK,
  );

  const loadPaymentLink = useCallback(
    (id: string) => fetchPaymentLink({ variables: { id } }),
    [fetchPaymentLink],
  );

  const savePaymentLink = useCallback(
    async (values: PaymentLinkFormValues, id?: string) => {
      const input = toPaymentLinkInput(values, !!id);

      try {
        if (id) {
          const { data } = await updatePaymentLink({
            variables: { id, input },
          });
          toast.success(t("Payment link updated"));
          return data?.updatePaymentLink as PaymentLink;
        }

        const { data } = await createPaymentLink({ variables: { input } });
        toast.success(t("Payment link created"));
        return data?.createPaymentLink as PaymentLink;
      } catch (error) {
        toast.error(messageOf(error) || t("Could not save this payment link"));
        return null;
      }
    },
    [createPaymentLink, updatePaymentLink, t],
  );

  const deactivate = useCallback(
    async (id: string, inactiveMessage?: string) => {
      try {
        await deactivatePaymentLink({ variables: { id, inactiveMessage } });
        toast.success(t("Payment link deactivated"));
        return true;
      } catch (error) {
        toast.error(
          messageOf(error) || t("Could not deactivate this payment link"),
        );
        return false;
      }
    },
    [deactivatePaymentLink, t],
  );

  const reactivate = useCallback(
    async (id: string) => {
      try {
        await reactivatePaymentLink({ variables: { id } });
        toast.success(t("Payment link reactivated"));
        return true;
      } catch (error) {
        toast.error(
          messageOf(error) || t("Could not reactivate this payment link"),
        );
        return false;
      }
    },
    [reactivatePaymentLink, t],
  );

  return {
    paymentLink,
    setPaymentLink,
    loadPaymentLink,
    savePaymentLink,
    deactivate,
    reactivate,
    loading:
      loadingPaymentLink ||
      creating ||
      updating ||
      deactivating ||
      reactivating,
  };
}

/* Omitted fields retain their stored values; linkType is immutable. */
function toPaymentLinkInput(values: PaymentLinkFormValues, isUpdate: boolean) {
  const isSubscription = values.linkType === PaymentLinkType.SUBSCRIPTION;
  const isFixed =
    isSubscription || values.amountMode === PaymentLinkAmountMode.FIXED;

  return {
    name: values.name.trim(),
    description: values.description?.trim() || null,
    ...(isUpdate ? {} : { linkType: values.linkType }),
    ...(isSubscription
      ? {
          interval: values.interval,
          maxPayments: values.maxPayments || null,
        }
      : {}),
    amountMode: isFixed ? PaymentLinkAmountMode.FIXED : values.amountMode,
    amount: isFixed ? values.amount : null,
    minAmount: isFixed ? null : values.minAmount,
    maxAmount: isFixed ? null : values.maxAmount,
    presetAmounts: isFixed ? [] : parsePresetAmounts(values.presetAmountsText),
    expiresAt: values.expiresAt,
    endsAt: values.endsAt ? new Date(values.endsAt).getTime() : null,
    feeCover: { enabled: values.feeCoverEnabled },
    tip: {
      enabled: values.tipEnabled,
      presetPercentages: parseTipPresets(values.tipPresetPercentages),
    },
    cashDiscount: {
      enabled: values.cashDiscountEnabled,
    },
    successBehavior: {
      type: values.successType,
      message:
        values.successType === PaymentLinkSuccessType.MESSAGE
          ? values.successMessage?.trim() || null
          : null,
      redirectUrl:
        values.successType === PaymentLinkSuccessType.REDIRECT
          ? values.successRedirectUrl?.trim() || null
          : null,
    },
  };
}

function messageOf(error: unknown): string | undefined {
  return (error as { message?: string })?.message;
}

export const parseTipPresets = (value: string): number[] =>
  value
    .split(",")
    .map((part) => Number(part.trim()))
    .filter((percentage) => Number.isFinite(percentage) && percentage > 0);

export const parsePresetAmounts = (value: string): number[] =>
  value
    .split(",")
    .map((part) => Number(part.trim()))
    .filter((amount) => Number.isFinite(amount) && amount > 0);
